import { useToast } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { FullDetailedMealPlanWithKeys } from '@web-app/server/api/types';
import { MEAL_PLAN_DETAILED_LOG_PREFIX } from '@web-app/utils/api/logger-prefixes';
import { useMolecule } from 'bunshi/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useEffect, useRef } from 'react';

import { MACROS } from '../components/CreateMealPlan/utils';
import { useSendDetailedMealPlan } from './mutations/meal-plan/send-detailed-meal-plan';
import { useUpdateDetailedMealPlan } from './mutations/meal-plan/update-detailed-meal-plan';

// ============================================================================
// Constants
// ============================================================================

// Delay before surfacing a "please keep this page open" toast while the send
// is still in flight. Picked to sit above the typical happy-path duration
// (~5-10s for fast sends) so the toast only appears when things are genuinely
// slow — e.g. PDF generation hiccups, Healthie retries on 429/5xx, or network
// weather. The toast copy is intentionally retry-agnostic since the client
// can't observe whether the server is actually retrying.
const LONG_RUNNING_SEND_TOAST_DELAY_MS = 10000;

// ============================================================================
// Types
// ============================================================================

export interface UseMealPlanMutationsReturn {
  // Mutations
  sendMealPlan: (displayNutritionalInformation: boolean) => Promise<boolean>;
  saveDetailedMealPlanEdit: () => Promise<FullDetailedMealPlanWithKeys | null>;

  // Loading states
  isSendingMealPlan: boolean;
  isMealPlanBusy: boolean;
  isMealPlanUpdating: boolean;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for meal plan edit and send mutations.
 *
 * Handles saving detailed meal plan edits (with optimistic updates and rollback)
 * and sending the final meal plan to the client.
 *
 * Generation mutations live in useMealPlanGeneration.
 */
export function useMealPlanMutations(): UseMealPlanMutationsReturn {
  const toast = useToast({
    position: 'bottom-left',
    isClosable: true,
    duration: 5000,
  });

  const {
    dailyRoomNameAtom,
    detailedMealPlanAtom,
    detailedMealPlanEditModeAtom,
    editDetailedMealPlanAtom,
    isMealPlanBusyAtom,
    isMealPlanSentAtom,
    isMealPlanUpdatingAtom,
    refetchDetailedMealPlanAtom,
  } = useMolecule(mealPlanMolecule);

  // Room name for API calls
  const dailyRoomName = useAtomValue(dailyRoomNameAtom);

  // Data atoms
  const [detailedMealPlan, setDetailedMealPlan] = useAtom(detailedMealPlanAtom);

  // Edit state
  const [editDetailedMealPlan, setEditDetailedMealPlan] = useAtom(
    editDetailedMealPlanAtom,
  );
  const setDetailedMealPlanEditMode = useSetAtom(detailedMealPlanEditModeAtom);

  // Loading states
  const [isMealPlanBusy, setIsMealPlanBusy] = useAtom(isMealPlanBusyAtom);
  const [isMealPlanUpdating, setIsMealPlanUpdating] = useAtom(
    isMealPlanUpdatingAtom,
  );

  // Broadcast sent state
  const setIsMealPlanSent = useSetAtom(isMealPlanSentAtom);

  // Refetch action atom — stable setter reference safe for useCallback deps.
  const refetchDetailedPlan = useSetAtom(refetchDetailedMealPlanAtom);

  // Existing mutation hooks
  const { mutateAsync: updateDetailedMealPlanMutation } =
    useUpdateDetailedMealPlan();

  const {
    mutateAsync: sendDetailedMealPlanMutation,
    isPending: isSendingMealPlan,
  } = useSendDetailedMealPlan();

  // ============================================================================
  // Long-running send toast
  // ============================================================================
  // If the send is still in flight after LONG_RUNNING_SEND_TOAST_DELAY_MS, show a
  // non-dismissable info toast so RDs don't reload the page while retries are happening.

  const longRunningToastIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!isSendingMealPlan) return;

    const timer = setTimeout(() => {
      longRunningToastIdRef.current =
        toast({
          title: 'Sending meal plan...',
          description:
            "Please keep this page open — we're finishing the delivery to Healthie.",
          status: 'warning',
          duration: null,
          isClosable: false,
        }) ?? null;
    }, LONG_RUNNING_SEND_TOAST_DELAY_MS);

    return () => {
      clearTimeout(timer);
      if (longRunningToastIdRef.current !== null) {
        toast.close(longRunningToastIdRef.current);
        longRunningToastIdRef.current = null;
      }
    };
  }, [isSendingMealPlan, toast]);

  // ============================================================================
  // Save Detailed Meal Plan Edit
  // ============================================================================

  const saveDetailedMealPlanEdit =
    useCallback(async (): Promise<FullDetailedMealPlanWithKeys | null> => {
      if (!detailedMealPlan || !editDetailedMealPlan) {
        setDetailedMealPlanEditMode(false);
        return null;
      }

      const oldData =
        detailedMealPlan.detailedMealPlan[editDetailedMealPlan.day - 1][
        editDetailedMealPlan.mealType
        ];

      // Check if anything actually changed
      if (
        oldData.body === editDetailedMealPlan.editorContent &&
        !MACROS.some(
          (key) => oldData?.[key] !== editDetailedMealPlan.macroData[key],
        )
      ) {
        setDetailedMealPlanEditMode(false);
        return detailedMealPlan;
      }

      const updatedDetailedMealPlan: FullDetailedMealPlanWithKeys =
        cloneDeep(detailedMealPlan);

      const dayIndex = editDetailedMealPlan.day - 1;
      const meal =
        updatedDetailedMealPlan.detailedMealPlan[dayIndex][
        editDetailedMealPlan.mealType
        ];

      // Update macro values
      MACROS.forEach((field) => {
        let value = editDetailedMealPlan.macroData[field];
        if (typeof value === 'string') {
          datadogLogs.logger.error('Invalid value for macro field', {
            field,
            value,
          });
          value = parseFloat(value);
        }
        meal[field] = value;
      });

      // Update body content
      meal.body = editDetailedMealPlan.editorContent;

      // Update local state optimistically
      setDetailedMealPlan(updatedDetailedMealPlan);

      // Persist to server
      if (dailyRoomName) {
        setIsMealPlanUpdating(true);
        try {
          await updateDetailedMealPlanMutation({
            dailyRoomName,
            payload: updatedDetailedMealPlan.detailedMealPlan,
          });
          await refetchDetailedPlan();
          setDetailedMealPlan(undefined); // clear override, let query through
        } catch (error) {
          datadogLogs.logger.error(
            `${MEAL_PLAN_DETAILED_LOG_PREFIX} Failed to save meal plan edit`,
            {
              dailyRoomName,
              day: editDetailedMealPlan.day,
              mealType: editDetailedMealPlan.mealType,
              err:
                error instanceof Error
                  ? { message: error.message, stack: error.stack }
                  : { message: String(error) },
            },
          );

          // Rollback optimistic update
          setDetailedMealPlan(detailedMealPlan);

          toast({
            title: 'Failed to update meal plan',
            status: 'error',
            duration: 3000,
          });

          return null;
        } finally {
          setIsMealPlanUpdating(false);
        }
      }

      setDetailedMealPlanEditMode(false);
      setEditDetailedMealPlan(null);
      return updatedDetailedMealPlan;
    }, [
      dailyRoomName,
      detailedMealPlan,
      refetchDetailedPlan,
      editDetailedMealPlan,
      setDetailedMealPlan,
      setDetailedMealPlanEditMode,
      setEditDetailedMealPlan,
      setIsMealPlanUpdating,
      toast,
      updateDetailedMealPlanMutation,
    ]);

  // ============================================================================
  // Send Meal Plan
  // ============================================================================

  const sendMealPlan = useCallback(
    async (displayNutritionalInformation: boolean): Promise<boolean> => {
      if (!detailedMealPlan) {
        datadogLogs.logger.warn(
          'Cannot send meal plan: no detailed meal plan',
          {
            feature: 'meal-plan',
            workflow: 'send-meal-plan',
          },
        );
        return false;
      }

      setIsMealPlanBusy(true);

      try {
        // Save any pending edits first
        let mealPlanToSend = detailedMealPlan;
        if (editDetailedMealPlan) {
          const saved = await saveDetailedMealPlanEdit();
          if (!saved) {
            return false;
          }
          mealPlanToSend = saved;
        }

        await sendDetailedMealPlanMutation({
          ...mealPlanToSend,
          displayNutritionalInformation,
        });

        await refetchDetailedPlan();
        setIsMealPlanSent(true);

        toast({
          title: 'Meal plan has been sent!',
          status: 'success',
          duration: 3000,
        });

        return true;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        datadogLogs.logger.error(
          `${MEAL_PLAN_DETAILED_LOG_PREFIX} Failed to send meal plan`,
          {
            dailyRoomName,
            err: { message: err.message, stack: err.stack },
          },
        );

        toast({
          title: 'Failed to send the meal plan. Please try again.',
          status: 'error',
          duration: 5000,
        });

        return false;
      } finally {
        setIsMealPlanBusy(false);
      }
    },
    [
      dailyRoomName,
      detailedMealPlan,
      editDetailedMealPlan,
      refetchDetailedPlan,
      saveDetailedMealPlanEdit,
      sendDetailedMealPlanMutation,
      setIsMealPlanBusy,
      setIsMealPlanSent,
      toast,
    ],
  );

  return {
    // Mutations
    sendMealPlan,
    saveDetailedMealPlanEdit,

    // Loading states
    isSendingMealPlan,
    isMealPlanBusy,
    isMealPlanUpdating,
  };
}
